import { Card, Typography, Empty, Spin, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import type { PieLabelRenderProps } from 'recharts';

const { Title, Text } = Typography;

interface HealthDistributionData {
  [key: string]: unknown;
  name: string;
  value: number;
  color: string;
  healthIndex?: string;
}

interface EquipmentHealthChartProps {
  data?: HealthDistributionData[];
  loading?: boolean;
}

export function EquipmentHealthChart({ data, loading }: EquipmentHealthChartProps) {
  const navigate = useNavigate();
  const { token } = theme.useToken();

  // 白文字が見えやすいよう固定色を使用（ダークモード・ライトモード共通）
  const COLORS = {
    excellent: '#389e0d', // 濃い緑
    good: '#096dd9',      // 濃い青
    warning: '#d48806',   // 濃いオレンジ
    critical: '#cf1322',  // 濃い赤
  };

  const chartData: HealthDistributionData[] = data || [
    { name: '優良（健全度70-100%）', value: 0, color: COLORS.excellent, healthIndex: 'excellent' },
    { name: '良好（健全度50-70%）', value: 0, color: COLORS.good, healthIndex: 'good' },
    { name: '注意（健全度30-50%）', value: 0, color: COLORS.warning, healthIndex: 'warning' },
    { name: '危険（健全度0-30%）', value: 0, color: COLORS.critical, healthIndex: 'critical' },
  ];

  const totalEquipments = chartData.reduce((sum, item) => sum + item.value, 0);
  const hasData = totalEquipments > 0;

  const handleClick = (data: HealthDistributionData) => {
    if (data.healthIndex) {
      navigate('/app/deterioration', { state: { healthIndex: data.healthIndex } });
    } else {
      navigate('/app/deterioration');
    }
  };

  const renderCustomLabel = (props: PieLabelRenderProps) => {
    const {
      cx = 0,
      cy = 0,
      midAngle = 0,
      innerRadius = 0,
      outerRadius = 0,
      percent = 0,
    } = props || {};
    if (percent === 0) return null;

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        stroke="rgba(0,0,0,0.4)"
        strokeWidth={2}
        paintOrder="stroke"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: '14px', fontWeight: 'bold' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>
            設備健全度分布
          </Title>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            集計対象設備数: {totalEquipments}台
          </Text>
        </div>
      }
      styles={{ body: { padding: '0 24px 24px' } }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      ) : !hasData ? (
        <Empty
          description="データがありません"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ padding: '40px 0' }}
        />
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Pie
              data={chartData}
              cx="50%"
              cy="42%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={100}
              innerRadius={60}
              fill="#8884d8"
              dataKey="value"
              onClick={handleClick}
              style={{ cursor: 'pointer' }}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => `${value}台`}
              contentStyle={{
                borderRadius: '8px',
                backgroundColor: token.colorBgElevated,
                border: `1px solid ${token.colorBorder}`,
                color: token.colorText,
                boxShadow: token.boxShadowSecondary,
              }}
              labelStyle={{ color: token.colorText }}
              itemStyle={{ color: token.colorText }}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              iconType="circle"
              wrapperStyle={{ bottom: 30, paddingTop: 0 }}
              formatter={(value, entry) => {
                const item = entry.payload as HealthDistributionData;
                return `${value} (${item.value}台)`;
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}
